import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gradeApi } from '../../api.js';
import { useAuth } from '../../auth/AuthContext';
import ConfirmationModal from '../ConfirmationModal'; // Correct import path
import { X } from 'lucide-react';
// import demoVideo from "./demo.mp4";
import { ViewSelectIcon } from '../ViewSelect';
import { FiMousePointer } from 'react-icons/fi';

function TClasses() {
  const [className, setClassName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false); // State for confirmation modal
  const [selectedClassId, setSelectedClassId] = useState(null); // State for selected class ID
  const modalRef = useRef();
  const videoModalRef = useRef();
  const [classes, setClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const { user } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isExportConfirmationModalOpen, setIsExportConfirmationModalOpen] = useState(false);
  const [selectedClassForExport, setSelectedClassForExport] = useState(null);
  const [exportScoreType, setExportScoreType] = useState('percentage'); // New state for score type

  const handleExportClass = classId => {
    setSelectedClassForExport(classId);
    setIsExportConfirmationModalOpen(true);
  };

  const confirmExportClass = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/courses/${selectedClassForExport}/export/`,
        config: {
          params: {
            score_type: exportScoreType
          },
          headers: {
            Authorization: `Bearer ${user.session_token}`
          },
          responseType: 'blob' // Important: This tells axios to treat the response as binary data
        }
      });

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });

      // Create a link element, use it to download the blob, and remove it
      const downloadLink = document.createElement('a');
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.download = `class_export_${selectedClassForExport}.csv`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setIsExportConfirmationModalOpen(false);
    } catch (error) {
      setErrorMessage('Failed to export class. Please try again.');
      setIsErrorVisible(true);
    }
  };

  function MoreAfter({ toggleBool, id }) {
    return (
      <div className="grid grid-cols-3 items-center gap-5 border border-gray-250 rounded-3xl p-2 max-w-28">
        <div className="size-5 flex items-center justify-center">
          <button onClick={() => handleDeleteClass(id)}>
            <img src="/assets/trash.png" className="opacity-70 size-5" alt="Delete Class" />
          </button>
        </div>
        <div className="size-5 flex items-center justify-center">
          <button onClick={() => handleExportClass(id)}>
            <img src="/assets/export.png" className="opacity-60 size-5" alt="Export Class CSV" />
          </button>
        </div>
        <div className="size-5 flex items-center justify-center">
          <button onClick={toggleBool}>
            <img src="/assets/more.png" className="opacity-70 size-3" alt="More" />
          </button>
        </div>
      </div>
    );
  }

  const getClasses = async () => {
    try {
      setIsLoading(true); // Set loading to true before fetching
      const response = await gradeApi.get({
        path: `/api/users/${user.user.id}/courses-lite/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      setClasses(response.data.courses);
    } catch (error) {
      setErrorMessage('Failed to fetch classes. Please try again.');
      setIsErrorVisible(true);
    } finally {
      setIsLoading(false); // Set loading to false when done, regardless of success or error
    }
    // console.log(classes);
  };

  useEffect(() => {
    getClasses();
  }, []);

  useEffect(() => {
    if (user.loginRecent) {
      setIsVideoModalOpen(true);
    }
  }, [user]);

  const handleSubmit = async e => {
    e.preventDefault();

    // Check class name length
    if (className.length > 70) {
      setErrorMessage('Class name must not exceed 70 characters.');
      setIsErrorVisible(true);
      return;
    }

    const classExists = classes.some(c => c.name === className);
    if (classExists) {
      setErrorMessage('Class with this name already exists.');
      setIsErrorVisible(true);
      return;
    }

    try {
      const response = await gradeApi.post({
        path: `/api/courses/`,
        body: {
          name: className,
          email: user.user.email // Add email to the course creation request
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      getClasses();
      setIsModalOpen(false);
    } catch (error) {
      setErrorMessage(`An error has occurred: ${error.response?.data?.error || ''}`);
      setIsErrorVisible(true);
    }
    getClasses();
    setClassName('');
  };

  const handleDeleteClass = classId => {
    setSelectedClassId(classId);
    setIsConfirmationModalOpen(true);
  };

  const confirmDeleteClass = async () => {
    try {
      await gradeApi.delete({
        path: `/api/courses/${selectedClassId}/`,
        headers: {
          Authorization: `Bearer ${user.session_token}`
        }
      });
      getClasses();
      setIsConfirmationModalOpen(false);
    } catch (error) {
      setErrorMessage('Failed to delete class. Please try again.');
      setIsErrorVisible(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalOpen(false);
      }
      if (videoModalRef.current && !videoModalRef.current.contains(event.target)) {
        setIsVideoModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalRef, videoModalRef]);

  const handleErrorMessageClick = () => {
    setIsErrorVisible(false);
    setErrorMessage('');
  };

  function MoreBefore({ toggleBool }) {
    return (
      <div className="grid grid-cols-3 items-center gap-5 border border-white rounded-3xl p-2 max-w-28">
        <div className="size-5 flex items-center justify-center"></div>
        <div className="size-5 flex items-center justify-center"></div>
        <div className="size-5 flex items-center justify-center">
          <button onClick={toggleBool}>
            <img src="/assets/more.png" className="opacity-70 size-3" alt="More" />
          </button>
        </div>
      </div>
    );
  }

  function MoreButton({ myBool, toggleBool, id }) {
    return myBool ? (
      <MoreBefore toggleBool={toggleBool} />
    ) : (
      <MoreAfter toggleBool={toggleBool} id={id} />
    );
  }

  const ClassCard = ({ id, isOpenToManage, name, code, num_students, instructors }) => {
    const [showOptions, setShowOptions] = useState(false);

    // Format instructor names, filtering out any with "test" in their names
    const instructorNames =
      instructors && instructors.length > 0
        ? instructors
            .filter(instructor => {
              const firstName = (instructor.first_name || '').toLowerCase();
              const lastName = (instructor.last_name || '').toLowerCase();
              return !firstName.includes('test') && !lastName.includes('test');
            })
            .map(instructor => `${instructor.first_name} ${instructor.last_name}`)
            .join(', ')
        : '';

    return (
      <Link to={`/tclasses/${id}/tassignments`}>
        <div className="relative bg-white rounded-3xl p-6 transition-all duration-300 hover:shadow-lg border border-gray-200 hover:border-orange-200">
          <div className="flex flex-col space-y-4">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <h3 className="font-medium text-gray-900 line-clamp-2">{name}</h3>
                <p className="text-sm text-gray-500">{code}</p>
                {instructorNames && <p className="text-sm text-gray-600">{instructorNames}</p>}
              </div>

              {isOpenToManage && (
                <div className="relative">
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowOptions(!showOptions);
                    }}
                  >
                    <img src="/assets/more.png" className="opacity-70 size-3" alt="More" />
                  </button>

                  {showOptions && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10">
                      <button
                        className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-orange-50 flex items-center space-x-2"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleExportClass(id);
                        }}
                      >
                        <img src="/assets/export.png" className="opacity-60 size-4" alt="Export" />
                        <span>Export Grades CSV</span>
                      </button>
                      <button
                        className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center space-x-2"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClass(id);
                        }}
                      >
                        <img src="/assets/trash.png" className="opacity-70 size-4" alt="Delete" />
                        <span>Delete Class</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">{num_students}</span>
                <span className="text-sm text-gray-500">students</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const ClassList = ({ classes }) => {
    // Define a condition for when to show the animation
    // Only show animation when there are no classes AND we're not loading
    const showAnimation = !isLoading && classes != null && classes.length === 0;

    // Define animation styles for the New Class button
    const newClassButtonStyles = {
      position: 'relative',
      ...(showAnimation && {
        filter: 'drop-shadow(0 0 30px rgba(228, 177, 122, 0.6))',
        animation: 'subtle-pulse 2.5s infinite',
        zIndex: 45 // Higher than overlay (30) but lower than modal (50)
      })
    };

    return (
      <div className="container mx-auto margin-below-topbar">
        {/* Full screen semi-transparent overlay when no classes are present */}
        {showAnimation && (
          <div 
            style={{
              position: 'fixed',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none',
              zIndex: 30, // Lowered z-index to be below the New Class button
              animation: 'fade-in 0.5s ease-in-out'
            }}
          ></div>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 min-w-20 relative">
          {/* New Class Card */}
          <button
            className="relative bg-white rounded-3xl p-6 transition-all duration-300 hover:shadow-lg border-2 border-dashed border-gray-200 hover:border-orange-200 group flex flex-col items-center justify-center"
            style={newClassButtonStyles}
            onClick={() => setIsModalOpen(true)}
          >
            <div className="flex flex-col items-center space-y-3">
              <div className="rounded-full bg-orange-50 p-3 group-hover:bg-orange-100 transition-colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-orange-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <span className="font-medium text-gray-600 group-hover:text-gray-900 transition-colors">
                New Class
              </span>

              {/* Add the animation elements conditionally */}
              {showAnimation && (
                <div
                  className="new-class-indicator"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '60%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                >
                  <div className="pointer-container" style={{ position: 'absolute' }}>
                    <FiMousePointer
                      className="mouse-pointer"
                      size={24}
                      color="#e4b17a"
                      style={{
                        filter: 'drop-shadow(0 0 3px rgba(228, 177, 122, 0.7))',
                        transform: 'rotate(-5deg)'
                      }}
                    />
                  </div>

                  {/* Ripple effect directly over the button */}
                  <div
                    className="click-ripple-container"
                    style={{
                      position: 'absolute',
                      width: '24px',
                      height: '24px',
                      opacity: 0.9
                    }}
                  >
                    <div className="click-ripple"></div>
                  </div>
                </div>
              )}
            </div>
          </button>

          {/* Existing Class Cards */}
          {classes && classes.length > 0 ? (
            classes.map(classItem => (
              <ClassCard
                key={classItem.id}
                isOpenToManage={classItem.isOpenToManage}
                id={classItem.id}
                name={classItem.name}
                code={classItem.code}
                num_students={classItem.num_students}
                instructors={classItem.instructors}
              />
            ))
          ) : (
            <p></p>
          )}
        </div>
      </div>
    );
  };

  const ClassRow = ({ classInfo }) => {
    const [myBool, setmyBool] = React.useState(true);

    function toggleBool() {
      setmyBool(!myBool);
    }

    return (
      <div className="hover:border rounded-full py-0.5 px-3 transition-all duration-100 desktop:hover:shadow-sm">
        <div className="grid grid-cols-4 align-middle">
          {/* <Link to={`/tassignments/${classInfo.id}`} className="classes-table-content"> */}
          <div className="classes-table-content">{classInfo.name}</div>
          {/* </Link> */}
          <div className="classes-table-content">{classInfo.code}</div>
          <div className="classes-table-content text-right">{classInfo.num_students}</div>
          {inOpenToManage && (
            <MoreButton myBool={myBool} toggleBool={toggleBool} id={classInfo.id} />
          )}
        </div>
      </div>
    );
  };

  // ClassTable component
  const ClassTable = ({ classes, handleDeleteClass }) => {
    return (
      <div className="min-w-full margin-below-topbar">
        <div className="grid grid-cols-4 gap-4 mb-6 p-2 font-bold border-b border-black ">
          <div className="classes-column-header">Class</div>
          <div className="classes-column-header">Class Code</div>
          <div className="classes-column-header text-right">Number of Students</div>
          <div className="classes-column-header text-right">Actions</div>
        </div>
        {classes ? (
          <div>
            {classes.map((value, i) => (
              <ClassRow key={`${i} + ${Math.random()}`} classInfo={value} />
            ))}
          </div>
        ) : (
          <div className="text-center">No classes available</div>
        )}
      </div>
    );
  };

  const [cardView, setCardView] = useState(true); // Changed to camelCase to follow convention

  // Corrected typo in function name
  function toggleCardView() {
    setCardView(!cardView);
  }

  // Your changeView function remains unchanged
  const changeView = ({ classes, handleDeleteClass }) => {
    return cardView ? (
      <ClassList classes={classes} handleDeleteClass={handleDeleteClass} />
    ) : (
      <ClassTable classes={classes} handleDeleteClass={handleDeleteClass} />
    );
  };

  return (
    <div id="HomeTeacher" className="flex flex-row px-6 justify-start">
      <div id="Main-Content-container" className="flex flex-col space-y-4 w-full">
        <div
          id="Overview"
          className="flex flex-row space-x-full justify-between fixed top-0 z-100 bg-white topbar-padding left-side-bar-size right-0 px-6"
        >
          <div className="flex flex-row items-center justify-center space-x-3 h-topbar-font-box-size">
            {/* <div>
              <ViewSelectIcon cardView={cardView} toggleCardView={toggleCardView} />
            </div> */}
            <h1 className="text-black topbar-text-size font-medium">My Classes</h1>
          </div>
        </div>

        <div>
          {changeView({ classes, handleDeleteClass })}
        </div>
      </div>
      <style jsx global>{`
        @keyframes subtle-pulse {
          0% {
            filter: drop-shadow(0 0 12px rgba(228, 177, 122, 0.4));
          }
          50% {
            filter: drop-shadow(0 0 21px rgba(228, 177, 122, 0.7));
          }
          100% {
            filter: drop-shadow(0 0 12px rgba(228, 177, 122, 0.4));
          }
        }

        .pointer-container {
          position: relative;
          animation: move-to-center 3s ease-in-out infinite;
        }

        @keyframes move-to-center {
          0% {
            transform: translate(20px, 20px);
          }
          40% {
            transform: translate(0, 0);
          }
          50% {
            transform: scale(0.9);
          }
          60% {
            transform: translate(0, 0) scale(1);
          }
          100% {
            transform: translate(20px, 20px);
          }
        }

        @keyframes float-vertical {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-2px);
          }
        }

        .mouse-pointer {
          filter: drop-shadow(0 0 1px rgba(228, 177, 122, 0.3));
          animation: pulse-opacity 2s ease-in-out infinite;
        }

        @keyframes pulse-opacity {
          0%,
          100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }

        .click-ripple-container {
          position: relative;
          animation: click-timing 3s infinite;
        }

        @keyframes click-timing {
          0%,
          45%,
          65%,
          100% {
            opacity: 0;
          }
          50%,
          55% {
            opacity: 1;
          }
        }

        .click-ripple {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(228, 177, 122, 0.6);
          border-radius: 50%;
          transform: scale(0);
          animation: click-ripple 1.5s infinite;
        }

        @keyframes click-ripple {
          0%,
          100% {
            transform: scale(0);
            opacity: 0;
          }
          10% {
            transform: scale(0);
            opacity: 0.8;
          }
          50% {
            transform: scale(10);
            opacity: 0;
          }
        }
        
        .screen-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.4);
          pointer-events: none;
          z-index: 10;
          animation: fade-in 0.5s ease-in-out;
          margin-top: 64px; /* Ensures the overlay starts below the toolbar */
        }
        
        @keyframes fade-in {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div
            ref={modalRef}
            className="relative mx-auto p-6 bg-white w-full max-w-[500px] rounded-xl shadow-xl"
            style={{
              boxShadow:
                '0 0 0 1px rgba(0,0,0,0.05), 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
            }}
          >
            <button
              className="absolute right-4 top-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
              onClick={() => setIsModalOpen(false)}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            <h2 className="text-xl font-semibold text-gray-900 mb-6">Create New Class</h2>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="relative">
                <input
                  type="text"
                  value={className}
                  className="w-full p-3 rounded-lg border border-gray-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
                  placeholder="Class Name"
                  onChange={e => {
                    setClassName(e.target.value);
                    setErrorMessage(
                      e.target.value.length > 70 ? 'Class name must not exceed 70 characters.' : ''
                    );
                  }}
                />
              </div>

              {errorMessage && <div className="text-red-500 text-sm">{errorMessage}</div>}

              <button
                type="submit"
                disabled={!className}
                className={`
                                    w-full px-5 py-2.5 rounded-xl text-[0.875rem] font-medium transition-all duration-200 hover:transform hover:-translate-y-0.5 shadow-sm hover:shadow
                                    ${
                                      !className
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-br from-[#e4b17a] to-[#c99b69] text-white hover:from-[#c99b69] hover:to-[#b38a5e]'
                                    }
                                `}
              >
                Create Class
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isExportConfirmationModalOpen}
        message="Are you sure you want to export this class as a CSV? Download will start automatically."
        onClose={() => setIsExportConfirmationModalOpen(false)}
        onConfirm={confirmExportClass}
      >
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Select score type:</label>
          <select
            value={exportScoreType}
            className="mt-1 block w-full p-2 rounded-3xl border border-gray-200 focus:border-orange-300 focus:ring-2 focus:ring-orange-100 outline-none transition-all"
            onChange={e => setExportScoreType(e.target.value)}
          >
            <option value="percentage">Percentage Scores</option>
            <option value="raw">Raw Scores</option>
          </select>
        </div>
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        message="Are you sure you want to delete this class? This action cannot be undone."
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={confirmDeleteClass}
      />
    </div>
  );
}

export default TClasses;
